import { SP } from "./namespace";
import { PASSIVE_TREES } from "../game/data";
import { passiveTipHTML, tipContextFromEl } from "../game/net-ui";
import { Sound } from "../game/audio";
import { SPELL_ORDER, clamp, escapeHtml, spellTipHTML } from "../client";

SP.synergyPreviewKey = (passiveId: string) => `syn:${passiveId}`;

function isSynergyPassive(passiveId: string) {
  for (const spellId of Object.keys(PASSIVE_TREES)) {
    for (const n of PASSIVE_TREES[spellId] || []) {
      if (n.id === passiveId && n.synergy) return true;
    }
  }
  return false;
}

SP.useSynergyPreview = function(passiveId: string) {
  if (!passiveId ||
    typeof window.wpreviewReset !== "function" ||
    typeof window.wpreviewStep !== "function") return false;
  return isSynergyPassive(passiveId);
};

SP.useSimPreview = function(spellId) {
  return typeof SPELL_ORDER !== "undefined" &&
    SPELL_ORDER.includes(spellId) &&
    spellId !== "frostspike" &&
    typeof window.wpreviewReset === "function" &&
    typeof window.wpreviewStep === "function";
};

SP.SimTip = {
  el: null,
  raf: null,
  spellId: null,
  passiveId: null,
  loopFill: null,
  lastT: 0,
  elapsed: 0,
  loop: 6.5,
  carry: null,
  _moveRaf: null,
  _movePending: null,
  _previewAudio: false,

  beginPreviewAudio() {
    if (this._previewAudio) return;
    Sound.pushPreviewAudio();
    this._previewAudio = true;
  },

  endPreviewAudio() {
    if (!this._previewAudio) return;
    if (this.carry?._btActive) {
      Sound.bulletTimeEnd(!!this.carry._btOwner);
      Sound.updateBulletTime(false, false);
    }
    Sound.popPreviewAudio();
    this._previewAudio = false;
  },

  freshCarry() {
    return {
      blizzards: [], firewalls: [], icewalls: [], hellrips: [],
      particles: [], shockwaves: [], beams: [], armPops: [], spellSpritePops: [], dmgPopups: [],
      spriteTrails: [], _trailLastPos: {},
    };
  },

  ensure() {
    if (!this.el) {
      this.el = document.createElement("div");
      this.el.className = "tooltip spell-preview-tip hidden";
      this.el.setAttribute("role", "tooltip");
      document.body.appendChild(this.el);
    }
    return this.el;
  },

  show(spellId, x, y, slotOverride) {
    if (!SP.useSimPreview(spellId) || !window.game) return false;
    this.endPreviewAudio();
    if (!window.wpreviewReset?.(spellId)) return false;
    this.beginPreviewAudio();
    this.spellId = spellId;
    this.passiveId = null;
    this.elapsed = 0;
    this.carry = this.freshCarry();
    this.loop = typeof window.wpreviewLoop === "function" ? window.wpreviewLoop() : 6.5;
    if (!this.loop) this.loop = 6.5;
    const el = this.ensure();
    const cap = SP.captionFor(spellId);
    const capHtml = cap
      ? `<p class="spell-preview-caption">${escapeHtml(cap)}</p>`
      : "";
    el.innerHTML =
      `<div class="spell-preview-tip-text">${spellTipHTML(spellId, slotOverride)}</div>` +
      capHtml +
      `<div class="spell-preview-stage spell-preview-stage-sim">` +
      `<canvas class="spell-preview-canvas" width="${SP.PREVIEW_W}" height="${SP.PREVIEW_H}" aria-hidden="true"></canvas>` +
      `<div class="spell-preview-loop" aria-hidden="true"><div class="spell-preview-loop-fill"></div></div>` +
      `</div>`;
    el.classList.remove("hidden");
    this.loopFill = el.querySelector(".spell-preview-loop-fill");
    this.canvas = el.querySelector(".spell-preview-canvas");
    this.ctx = this.canvas.getContext("2d");
    this.lastT = performance.now() / 1000;
    this.stopLoop();
    const tick = () => {
      if (!this.ctx || this.spellId !== spellId) return;
      const now = performance.now() / 1000;
      const dt = clamp(now - this.lastT, 0.001, 0.05);
      this.lastT = now;
      this.elapsed += dt;
      const raw = window.wpreviewStep?.(dt);
      let msg;
      try { msg = JSON.parse(raw); } catch (e) { return; }
      if (!msg || !Array.isArray(msg.p)) return;
      if (msg.loop) this.loop = msg.loop;
      try {
        (window.game as unknown as { renderSimPreview: (...args: unknown[]) => void }).renderSimPreview(
          this.ctx, this.canvas.width, this.canvas.height, msg, dt, this.carry);
      } catch (err) {
        console.warn("spell sim preview render failed:", err);
        return;
      }
      const prog = this.loop > 0 ? (this.elapsed % this.loop) / this.loop : 0;
      if (this.loopFill) this.loopFill.style.width = (prog * 100) + "%";
      this.raf = requestAnimationFrame(tick);
    };
    tick();
    this.move(x, y, true);
    requestAnimationFrame(() => this.move(x, y, true));
    return true;
  },

  showSynergy(spellId, passiveId, x, y, tipEl) {
    if (!SP.useSynergyPreview(passiveId) || !window.game) return false;
    this.endPreviewAudio();
    const key = SP.synergyPreviewKey(passiveId);
    if (!window.wpreviewReset?.(key)) return false;
    this.beginPreviewAudio();
    this.spellId = null;
    this.passiveId = passiveId;
    this.elapsed = 0;
    this.carry = this.freshCarry();
    this.loop = typeof window.wpreviewLoop === "function" ? window.wpreviewLoop() : 6.5;
    if (!this.loop) this.loop = 6.5;
    const el = this.ensure();
    const tipCtx = tipEl ? tipContextFromEl(tipEl) : { spellId, passives: {} };
    el.innerHTML =
      `<div class="spell-preview-tip-text">${passiveTipHTML(spellId, passiveId, tipCtx)}</div>` +
      `<div class="spell-preview-stage spell-preview-stage-sim">` +
      `<canvas class="spell-preview-canvas" width="${SP.PREVIEW_W}" height="${SP.PREVIEW_H}" aria-hidden="true"></canvas>` +
      `<div class="spell-preview-loop" aria-hidden="true"><div class="spell-preview-loop-fill"></div></div>` +
      `</div>`;
    el.classList.remove("hidden");
    this.loopFill = el.querySelector(".spell-preview-loop-fill");
    this.canvas = el.querySelector(".spell-preview-canvas");
    this.ctx = this.canvas.getContext("2d");
    this.lastT = performance.now() / 1000;
    this.stopLoop();
    const tick = () => {
      if (!this.ctx || this.passiveId !== passiveId) return;
      const now = performance.now() / 1000;
      const dt = clamp(now - this.lastT, 0.001, 0.05);
      this.lastT = now;
      this.elapsed += dt;
      const raw = window.wpreviewStep?.(dt);
      let msg;
      try { msg = JSON.parse(raw); } catch (e) { return; }
      if (!msg || !Array.isArray(msg.p)) return;
      if (msg.loop) this.loop = msg.loop;
      try {
        (window.game as unknown as { renderSimPreview: (...args: unknown[]) => void }).renderSimPreview(
          this.ctx, this.canvas.width, this.canvas.height, msg, dt, this.carry);
      } catch (err) {
        console.warn("synergy sim preview render failed:", err);
        return;
      }
      const prog = this.loop > 0 ? (this.elapsed % this.loop) / this.loop : 0;
      if (this.loopFill) this.loopFill.style.width = (prog * 100) + "%";
      this.raf = requestAnimationFrame(tick);
    };
    tick();
    this.move(x, y, true);
    requestAnimationFrame(() => this.move(x, y, true));
    return true;
  },

  move(x, y, immediate) {
    if (!immediate) {
      this._scheduleMove(x, y);
      return;
    }
    const el = this.el;
    if (!el || el.classList.contains("hidden")) return;
    const r = el.getBoundingClientRect();
    let nx = x + 18, ny = y + 14;
    if (nx + r.width > window.innerWidth - 10) nx = x - r.width - 18;
    if (ny + r.height > window.innerHeight - 10) ny = y - r.height - 14;
    if (nx < 8) nx = 8;
    if (ny < 8) ny = 8;
    el.style.left = nx + "px";
    el.style.top = ny + "px";
  },

  _scheduleMove(x, y) {
    this._movePending = { x, y };
    if (this._moveRaf) return;
    this._moveRaf = requestAnimationFrame(() => {
      this._moveRaf = null;
      const p = this._movePending;
      if (!p) return;
      this.move(p.x, p.y, true);
    });
  },

  stopLoop() {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
  },

  hide() {
    if (this._moveRaf) {
      cancelAnimationFrame(this._moveRaf);
      this._moveRaf = null;
    }
    this._movePending = null;
    this.endPreviewAudio();
    this.stopLoop();
    this.spellId = null;
    this.passiveId = null;
    this.loopFill = null;
    this.ctx = null;
    this.canvas = null;
    this.carry = null;
    if (this.el) this.el.classList.add("hidden");
  },
};
